/* Wade Greens - Growing Zone map renderer. Loaded from wg-schema-global.liquid.

   Draws the real USDA hardiness map, not a state-shaded approximation.
   assets/wg-zone-grid.txt is a run-length-encoded raster of the 2023 USDA /
   PRISM zone data (180x114 cells, Albers Equal Area Conic), built from the
   40,284-ZIP bulk dataset published at phzmapi.org. The plant's own zones are
   drawn at full strength with the rest of the country faded behind, so the
   tab answers "where does THIS grow" while still reading as a zone map.

   assets/wg-zone-map.svg supplies the state outlines: it clips the raster to
   the coastline and draws the borders on top. Both files share one coordinate
   space, 1000x634 units, using the projection constants below.

   wg-ui.js owns discovery and the ZIP lookup; this file only paints. */
(function () {
  'use strict';

  var RAMP = {
    2: '#5b3a8e', 3: '#6a49a0', 4: '#4257b5', 5: '#3f74c8', 6: '#3e93d4',
    7: '#46abd8', 8: '#4cc0cf', 9: '#4fc9ad', 10: '#57c98a', 11: '#6ec96a',
    12: '#8fd05c', 13: '#b3d955', 14: '#d3e04f', 15: '#eddc4b', 16: '#f5c944',
    17: '#f6b03c', 18: '#f39433', 19: '#ee782d', 20: '#e65c28', 21: '#dc4325',
    22: '#cc2f28', 23: '#b8242c', 24: '#a01e33', 25: '#85193a', 26: '#6b1540',
    27: '#521145'
  };
  var DIM = 42, W = 1000, H = 634;
  var AL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  /* Albers Equal Area Conic, standard parallels 29.5/45.5, origin -96/23 -
     the same numbers the SVG and the raster were generated with. */
  var PN = 0.6028370046, PC = 1.3512213254, PR = 1.5562263295;
  var LON0 = -96 * Math.PI / 180;
  var MINX = -0.3696228371, MAXX = 0.3528652515;
  var MINY = 0.0408714336, MAXY = 0.4985223412;

  function project(lon, lat) {
    var rho = Math.sqrt(PC - 2 * PN * Math.sin(lat * Math.PI / 180)) / PN;
    var th = PN * (lon * Math.PI / 180 - LON0);
    var x = rho * Math.sin(th), y = PR - rho * Math.cos(th);
    return [(x - MINX) / (MAXX - MINX) * W, (MAXY - y) / (MAXY - MINY) * H];
  }

  function decode(txt) {
    var nl = txt.indexOf('\n');
    var head = txt.slice(0, nl).split(' ');
    var cols = +head[1], rows = +head[2];
    var s = txt.slice(nl + 1);
    var g = new Uint8Array(cols * rows), p = 0, i = 0;
    while (i < s.length) {
      var v = AL.indexOf(s[i++]);
      if (v < 0) continue;
      var n = 0;
      while (i < s.length && s[i] >= '0' && s[i] <= '9') n = n * 10 + (+s[i++]);
      g.fill(v, p, p + n);
      p += n;
    }
    return { g: g, cols: cols, rows: rows };
  }

  window.wgZoneMap = {
    ramp: RAMP,
    dim: DIM,

    /* Returns a draw(markerLonLat) function, or null if anything is missing. */
    build: function (canvas, gridTxt, svgTxt, lo, hi) {
      if (!canvas || !canvas.getContext) return null;
      var grid = decode(gridTxt);
      var ds = [], re = / d="([^"]+)"/g, m;
      while ((m = re.exec(svgTxt))) ds.push(m[1]);
      if (!ds.length) return null;

      /* raster at grid resolution, scaled up with smoothing on the way out */
      var small = document.createElement('canvas');
      small.width = grid.cols;
      small.height = grid.rows;
      var sctx = small.getContext('2d');
      var img = sctx.createImageData(grid.cols, grid.rows);
      for (var k = 0; k < grid.g.length; k++) {
        var v = grid.g[k], o = k * 4;
        if (!v) { img.data[o + 3] = 0; continue; }
        var hex = RAMP[v] || '#cccccc';
        img.data[o] = parseInt(hex.slice(1, 3), 16);
        img.data[o + 1] = parseInt(hex.slice(3, 5), 16);
        img.data[o + 2] = parseInt(hex.slice(5, 7), 16);
        img.data[o + 3] = (v >= lo && v <= hi) ? 255 : DIM;
      }
      sctx.putImageData(img, 0, 0);

      var paths = ds.map(function (d) { return new Path2D(d); });
      var clip = new Path2D();
      paths.forEach(function (p) { clip.addPath(p); });

      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      var ctx = canvas.getContext('2d');

      return function draw(marker) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, W, H);
        ctx.save();
        ctx.clip(clip);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(small, 0, 0, W, H);
        ctx.restore();
        ctx.lineWidth = 1.1;
        ctx.strokeStyle = 'rgba(255,255,255,0.82)';
        paths.forEach(function (p) { ctx.stroke(p); });
        if (marker) {
          var xy = project(marker[0], marker[1]);
          ctx.beginPath();
          ctx.arc(xy[0], xy[1], 9, 0, Math.PI * 2);
          ctx.lineWidth = 3.5;
          ctx.strokeStyle = '#ffffff';
          ctx.stroke();
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#1a1a1a';
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(xy[0], xy[1], 3.2, 0, Math.PI * 2);
          ctx.fillStyle = '#1a1a1a';
          ctx.fill();
        }
      };
    },

    /* Colour key: every half-zone, the plant's own range at full strength. */
    ramps: function (host, lo, hi) {
      if (!host) return;
      host.innerHTML = '';
      for (var v = 2; v <= 27; v++) {
        var cell = document.createElement('span');
        cell.className = 'wg-zone__rampcell' + (v >= lo && v <= hi ? ' is-in' : '');
        cell.style.background = RAMP[v];
        if (v % 2 === 0) {
          var lab = document.createElement('i');
          lab.textContent = v / 2;
          cell.appendChild(lab);
        }
        host.appendChild(cell);
      }
    }
  };
})();
